import React from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';

const DashboardContainer = styled.div`
  display: grid;
  gap: 20px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const MetricCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const MetricIcon = styled.div`
  font-size: 24px;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.background || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
`;

const MetricTitle = styled.h3`
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const MetricSubtext = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const ChartTitle = styled.h3`
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CommodityBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CommodityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
`;

const CommodityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CommodityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color || '#667eea'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const CommodityDetails = styled.div``;

const CommodityName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const CommodityQuantity = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const CommodityValue = styled.div`
  text-align: right;
`;

const CommodityPrice = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const CommodityPercentage = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const AlertsSection = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
`;

const AlertItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: ${props => props.type === 'warning' ? '#fff3cd' : '#f8d7da'};
  border-left: 4px solid ${props => props.type === 'warning' ? '#ffc107' : '#dc3545'};

  &:last-child {
    margin-bottom: 0;
  }
`;

const AlertIcon = styled.div`
  font-size: 20px;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  margin-bottom: 4px;
`;

const AlertDescription = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const SyncIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.inSync ? '#28a745' : '#dc3545'};
  font-weight: 500;
`;

const SyncDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.inSync ? '#28a745' : '#dc3545'};
  animation: ${props => props.inSync ? 'none' : 'pulse 2s infinite'};

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const InventoryDashboard = () => {
  const {
    inventoryItems,
    totalValue,
    commodityBreakdown,
    syncStatus,
    lowStockAlerts,
    expiringItems,
    warehouses
  } = useInventory();

  const commodityColors = {
    Sugar: '#ff6b6b',
    Coffee: '#4ecdc4',
    Gold: '#ffe66d',
    Silver: '#a8e6cf',
    Copper: '#ff8b94',
    Wheat: '#ffd93d'
  };

  const commodityIcons = {
    Sugar: '🍯',
    Coffee: '☕',
    Gold: '🥇',
    Silver: '🥈',
    Copper: '🔶',
    Wheat: '🌾'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const totalItems = inventoryItems.length;
  const totalTokensMinted = inventoryItems.reduce((sum, item) => sum + item.tokensMinted, 0);
  const syncPercentage = syncStatus.inSync ? 100 : Math.max(0, 100 - (syncStatus.pendingOperations * 20));

  return (
    <DashboardContainer>
      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard>
          <MetricHeader>
            <MetricTitle>Total Portfolio Value</MetricTitle>
            <MetricIcon background="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">💰</MetricIcon>
          </MetricHeader>
          <MetricValue>{formatCurrency(totalValue)}</MetricValue>
          <MetricSubtext>Across {totalItems} inventory items</MetricSubtext>
          <ProgressBar>
            <ProgressFill percentage={100} />
          </ProgressBar>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>Total Items</MetricTitle>
            <MetricIcon background="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">📦</MetricIcon>
          </MetricHeader>
          <MetricValue>{totalItems}</MetricValue>
          <MetricSubtext>Active inventory items</MetricSubtext>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>Tokens Minted</MetricTitle>
            <MetricIcon background="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">🪙</MetricIcon>
          </MetricHeader>
          <MetricValue>{formatNumber(totalTokensMinted)}</MetricValue>
          <MetricSubtext>Total tokens in circulation</MetricSubtext>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>Warehouses</MetricTitle>
            <MetricIcon background="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)">🏢</MetricIcon>
          </MetricHeader>
          <MetricValue>{warehouses.length}</MetricValue>
          <MetricSubtext>Active storage facilities</MetricSubtext>
        </MetricCard>
      </MetricsGrid>

      {/* Charts and Breakdowns */}
      <ChartsGrid>
        {/* Commodity Breakdown */}
        <ChartCard>
          <ChartTitle>📊 Commodity Breakdown</ChartTitle>
          <CommodityBreakdown>
            {Object.entries(commodityBreakdown).map(([commodity, data]) => (
              <CommodityItem key={commodity}>
                <CommodityInfo>
                  <CommodityIcon color={commodityColors[commodity]}>
                    {commodityIcons[commodity] || '📦'}
                  </CommodityIcon>
                  <CommodityDetails>
                    <CommodityName>{commodity}</CommodityName>
                    <CommodityQuantity>{formatNumber(data.quantity)} units</CommodityQuantity>
                  </CommodityDetails>
                </CommodityInfo>
                <CommodityValue>
                  <CommodityPrice>{formatCurrency(data.value)}</CommodityPrice>
                  <CommodityPercentage>{data.percentage}%</CommodityPercentage>
                </CommodityValue>
              </CommodityItem>
            ))}
          </CommodityBreakdown>
        </ChartCard>

        {/* Sync Status */}
        <ChartCard>
          <ChartTitle>
            🔗 Blockchain Sync Status
            <SyncIndicator inSync={syncStatus.inSync}>
              <SyncDot inSync={syncStatus.inSync} />
              {syncStatus.inSync ? 'In Sync' : 'Out of Sync'}
            </SyncIndicator>
          </ChartTitle>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>
              {syncPercentage}%
            </div>
            <ProgressBar>
              <ProgressFill percentage={syncPercentage} />
            </ProgressBar>
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            Last sync: {syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleString() : 'Never'}
          </div>
          {syncStatus.pendingOperations > 0 && (
            <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '8px' }}>
              {syncStatus.pendingOperations} pending operations
            </div>
          )}
        </ChartCard>
      </ChartsGrid>

      {/* Alerts Section */}
      {(lowStockAlerts.length > 0 || expiringItems.length > 0) && (
        <AlertsSection>
          <ChartTitle>🚨 Active Alerts</ChartTitle>
          
          {lowStockAlerts.map((alert, index) => (
            <AlertItem key={`low-stock-${index}`} type="warning">
              <AlertIcon>⚠️</AlertIcon>
              <AlertContent>
                <AlertTitle>Low Stock Alert</AlertTitle>
                <AlertDescription>{alert.message || 'Stock levels below threshold'}</AlertDescription>
              </AlertContent>
            </AlertItem>
          ))}

          {expiringItems.map((item, index) => (
            <AlertItem key={`expiring-${index}`} type="danger">
              <AlertIcon>⏰</AlertIcon>
              <AlertContent>
                <AlertTitle>Item Expiring Soon</AlertTitle>
                <AlertDescription>{item.message || 'Item approaching expiration date'}</AlertDescription>
              </AlertContent>
            </AlertItem>
          ))}

          {lowStockAlerts.length === 0 && expiringItems.length === 0 && (
            <AlertItem type="success">
              <AlertIcon>✅</AlertIcon>
              <AlertContent>
                <AlertTitle>All Systems Normal</AlertTitle>
                <AlertDescription>No active alerts at this time</AlertDescription>
              </AlertContent>
            </AlertItem>
          )}
        </AlertsSection>
      )}
    </DashboardContainer>
  );
};

export default InventoryDashboard; 